import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, ShieldCheck, FileSignature, Sprout,
  Coins, Globe, Wallet, Leaf, Fish,
} from 'lucide-react';
import { PILLARS, OFFER_STATS, IMPACT_STATS, IMAGES } from '../data/mock';
import { getProjects } from '../lib/api';
import PlatformPreview from '../components/PlatformPreview';
import ProjectCard from '../components/ProjectCard';
import ProjectDetail from '../components/ProjectDetail';
import CTASection from '../components/CTASection';
import { useConnect } from '../context/ConnectContext';
import useReveal from '../hooks/useReveal';

const iconMap = { ShieldCheck, FileSignature, Sprout, Coins, Globe, Wallet };
const KEYWORDS = ['Calm', 'Minimal', 'Purposeful', 'Trustworthy', 'Natural Geometry', 'Earth-first', 'Premium', 'Cyclical', 'Measured Growth'];

export default function Home() {
  const { openConnect } = useConnect();
  const [selected, setSelected] = useState(null);
  const [featured, setFeatured] = useState([]);
  useReveal([featured]);

  useEffect(() => {
    getProjects()
      .then((data) => setFeatured(data.filter((p) => p.metrics).slice(0, 3)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative flex min-h-screen items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroAerial} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-farmway-green via-farmway-green/85 to-farmway-green-dark/90" />
          <div className="absolute inset-0 bg-farmway-green/30" />
        </div>
        <div className="container-fw relative pt-28">
          <span className="eyebrow animate-fade-up">Farm <span className="text-farmway-cream/40">·</span> Food <span className="text-farmway-cream/40">·</span> Future</span>
          <h1 className="mt-6 max-w-4xl animate-fade-up" style={{ animationDelay: '80ms' }}>
            <span className="block font-body text-2xl font-light text-farmway-cream/70 sm:text-3xl">Your</span>
            <span className="display-xl block text-6xl text-farmway-cream sm:text-7xl lg:text-[7.5rem]">Investment</span>
            <span className="display-xl block text-5xl text-farmway-gold sm:text-6xl lg:text-7xl">Platform</span>
          </h1>
          <p className="mt-7 max-w-xl animate-fade-up text-lg leading-relaxed text-farmway-cream/80" style={{ animationDelay: '160ms' }}>
            For future-proof farmland and agribusiness projects, democratizing access to
            sustainable food investments across the world.
          </p>
          <div className="mt-9 flex animate-fade-up flex-wrap items-center gap-4" style={{ animationDelay: '240ms' }}>
            <Link to="/investment-opportunities" className="btn-gold">
              Explore Opportunities <ArrowRight className="h-4 w-4" />
            </Link>
            <button onClick={openConnect} className="btn-outline">Connect</button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-farmway-green-dark/40 backdrop-blur-sm">
          <div className="container-fw grid grid-cols-2 divide-x divide-white/10 sm:grid-cols-4">
            {IMPACT_STATS.map((s) => (
              <div key={s.label} className="px-2 py-5 text-center">
                <p className="font-display text-2xl font-black text-farmway-gold sm:text-3xl">{s.value}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wider text-farmway-cream/60">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KEYWORD MARQUEE */}
      <div className="overflow-hidden border-y border-white/10 bg-farmway-green-mid/30 py-4">
        <div className="flex w-max animate-marquee items-center gap-8">
          {[...KEYWORDS, ...KEYWORDS].map((k, i) => (
            <span key={`${k}-${i}`} className="flex items-center gap-8 font-display text-sm font-semibold uppercase tracking-[0.25em] text-farmway-cream/45">
              {k} <Leaf className="h-3.5 w-3.5 text-farmway-leaf" />
            </span>
          ))}
        </div>
      </div>

      {/* PILLARS */}
      <section className="py-24">
        <div className="container-fw">
          <div className="max-w-2xl reveal">
            <span className="eyebrow">Why Farmway</span>
            <h2 className="mt-4 display-xl text-4xl text-farmway-cream sm:text-5xl">A resilient asset class, reimagined</h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PILLARS.map((p, i) => {
              const Icon = iconMap[p.icon];
              return (
                <div key={p.title} className="reveal group rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-farmway-leaf/40" data-delay={i * 100}>
                  <div className="grid h-14 w-14 place-items-center rounded-xl bg-farmway-gold/15 text-farmway-gold transition-colors group-hover:bg-farmway-gold group-hover:text-farmway-green">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold text-farmway-cream">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-farmway-cream/70">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER TEASER */}
      <section className="relative overflow-hidden py-24">
        <div className="container-fw grid items-center gap-14 lg:grid-cols-2">
          <div className="reveal">
            <span className="eyebrow">What we offer</span>
            <h2 className="mt-4 display-xl text-4xl text-farmway-cream sm:text-5xl">Worldwide investment options</h2>
            <p className="mt-5 max-w-lg text-farmway-cream/75">
              Support the growth of sustainable farming and agribusinesses by enabling fractional
              investment, from anyone, anywhere.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {OFFER_STATS.map((s) => {
                const Icon = iconMap[s.icon];
                return (
                  <div key={s.label} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <Icon className="h-6 w-6 text-farmway-gold" />
                    <p className="mt-4 font-display text-xl font-black text-farmway-cream">{s.value}</p>
                    <p className="mt-1 text-xs text-farmway-cream/55">{s.label}</p>
                  </div>
                );
              })}
            </div>
            <Link to="/what-we-offer" className="mt-8 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-farmway-gold hover:gap-3 transition-all">
              Discover our approach <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="reveal relative" data-delay="120">
            <div className="grid grid-cols-2 gap-4">
              <img src={IMAGES.lushGreen} alt="Farmland" className="h-64 w-full rounded-2xl object-cover" />
              <img src={IMAGES.almondAerial} alt="Orchard" className="mt-8 h-64 w-full rounded-2xl object-cover" />
            </div>
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 rounded-2xl border border-white/10 bg-farmway-green-dark/90 px-6 py-4 backdrop-blur-md">
              <p className="text-center font-display text-sm font-bold uppercase tracking-wider text-farmway-gold">Fiat &amp; Crypto accepted</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED OPPORTUNITIES */}
      <section className="py-24">
        <div className="container-fw">
          <div className="flex flex-wrap items-end justify-between gap-6 reveal">
            <div className="max-w-xl">
              <span className="eyebrow">Investment opportunities</span>
              <h2 className="mt-4 display-xl text-4xl text-farmway-cream sm:text-5xl">Featured projects</h2>
            </div>
            <Link to="/investment-opportunities" className="btn-outline">View all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((p, i) => (
              <div key={p.id} className="reveal" data-delay={i * 100}>
                <ProjectCard project={p} onOpen={setSelected} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM PREVIEW */}
      <section className="relative overflow-hidden bg-farmway-green-dark py-24">
        <div className="container-fw grid items-center gap-16 lg:grid-cols-2">
          <div className="reveal">
            <span className="eyebrow">The platform</span>
            <h2 className="mt-4 display-xl text-4xl text-farmway-cream sm:text-5xl">A state-of-the-art investment platform</h2>
            <p className="mt-5 max-w-lg text-farmway-cream/75">
              The upcoming Farmway app guides you through discovering projects, building a portfolio,
              and tracking real-world yield, all in one calm, considered experience.
            </p>
            <ul className="mt-8 space-y-4">
              {['Discover curated global projects', 'Buy & sell fractional ownership', 'Track land, yield & returns in real time'].map((t) => (
                <li key={t} className="flex items-center gap-3 text-farmway-cream/85">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-farmway-gold/15 text-farmway-gold"><Leaf className="h-3.5 w-3.5" /></span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal flex justify-center" data-delay="120">
            <PlatformPreview />
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="py-24">
        <div className="container-fw grid items-center gap-14 lg:grid-cols-2">
          <div className="reveal relative overflow-hidden rounded-3xl">
            <img src={IMAGES.farmerPortrait} alt="Farmer" className="h-[460px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-farmway-green-dark/70 to-transparent" />
            <div className="absolute bottom-6 left-6 flex items-center gap-3">
              <Fish className="h-5 w-5 text-farmway-gold" />
              <span className="font-display text-sm font-semibold uppercase tracking-wider text-farmway-cream">Farms · Food · Future · Fish</span>
            </div>
          </div>
          <div className="reveal" data-delay="120">
            <span className="eyebrow">Our impact</span>
            <h2 className="mt-4 display-xl text-4xl text-farmway-cream sm:text-5xl">Growing tomorrow, greener</h2>
            <p className="mt-5 text-farmway-cream/75">
              We are committed to positively impacting our world by contributing to
              <span className="text-farmway-cream"> 9 of the UN&apos;s 17 Sustainable Development Goals</span>,
              regenerating land while nourishing a growing world.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {IMPACT_STATS.map((s) => (
                <div key={s.label} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <p className="font-display text-2xl font-black text-farmway-gold">{s.value}</p>
                  <p className="mt-1 text-xs text-farmway-cream/60">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
      <ProjectDetail project={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
